import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Textarea,
  toast,
  Tooltip,
} from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { IconEdit } from "@tabler/icons-react";
import { updateMemory } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import { Memory } from "./types";
import { Teammate } from "@/pages/mcp/mcpSlice";

const editFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  memory: z.string().min(1, "Memory content is required"),
});

type EditFormValues = z.infer<typeof editFormSchema>;

interface EditMemoryProps {
  memory: Memory;
  datamate?: Teammate;
  showButtonText?: boolean;
}

const EditMemory = ({ memory, showButtonText }: EditMemoryProps) => {
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const { loggedinUser: currentUser } = useAppState();
  const queryClient = useQueryClient();

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      title: memory?.metadata?.title || "",
      memory: memory?.memory || "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; title: string; memory: string }) =>
      updateMemory(data.id, {
        memory: data.memory,
        metadata: {
          ...memory.metadata,
          title: data.title,
        },
      }),
    onSuccess: () => {
      toast({ title: "Memory updated successfully", variant: "default" });
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      setIsEditSheetOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update memory",
        variant: "destructive",
        description: error.message,
      });
    },
  });

  const handleEditSubmit = async (data: EditFormValues) => {
    if (!memory?.id) return;
    updateMutation.mutate({
      id: memory.id,
      title: data.title,
      memory: data.memory,
    });
  };

  // Only show edit button if current user created the memory
  if (!currentUser || memory?.user_id !== currentUser.uuid) {
    return null;
  }

  return (
    <>
      <Tooltip title="Edit Memory">
        <Button
          variant={showButtonText ? "outline" : "ghost"}
          size={showButtonText ? "sm" : "icon"}
          aria-label="Edit Memory"
          onClick={() => {
            form.reset({
              title: memory?.metadata?.title || "",
              memory: memory?.memory || "",
            });
            setIsEditSheetOpen(true);
          }}
        >
          <IconEdit className="al-w-4 al-h-4" />
          {showButtonText && "Edit Memory"}
        </Button>
      </Tooltip>
      {/* Edit Memory Sheet */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Memory</SheetTitle>
          </SheetHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleEditSubmit)} className="al-space-y-4 al-mt-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter memory title..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="memory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memory</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter memory content..."
                        className="al-min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Stack gap="md" className="al-pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditSheetOpen(false)}
                  className="al-flex-1"
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  loading={updateMutation.isPending}
                  className="al-flex-1"
                >
                  Update Memory
                </LoadingButton>
              </Stack>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default EditMemory;
